import { type ReactNode, useCallback, useId, useState } from 'react';
import { Text, tokens } from '@/design';
import type { LayerSourceDiagnostics, LayerRenderPathDiagnostics } from '@/modules/layer-diagnostics';
import type { NativeVectorStylePatch } from '@/engines/giro3d/nativePmtilesVectorLayer';

export interface PmtilesControlsProps {
  readonly source: LayerSourceDiagnostics;
  readonly renderPath: LayerRenderPathDiagnostics;
  readonly onStyleChange?: (patch: NativeVectorStylePatch) => void;
  readonly onSourceLayerChange?: (sourceLayer: string) => void;
}

export function PmtilesControls({ source, renderPath, onStyleChange, onSourceLayerChange }: PmtilesControlsProps) {
  const uid = useId();
  const [color, setColor] = useState<string>(tokens.color.semantic.info);
  const [fillOpacity, setFillOpacity] = useState(0.24);
  const [lineOpacity, setLineOpacity] = useState(1);
  const [lineWidth, setLineWidth] = useState(2);
  const [pointOpacity, setPointOpacity] = useState(1);
  const [pointRadius, setPointRadius] = useState(4);
  const [showFills, setShowFills] = useState(true);
  const [showLines, setShowLines] = useState(true);
  const [showPoints, setShowPoints] = useState(true);

  const emit = useCallback((partial: Partial<NativeVectorStylePatch>) => {
    onStyleChange?.({ color, fillOpacity, lineOpacity, lineWidth, pointOpacity, pointRadius, showFills, showLines, showPoints, ...partial });
  }, [color, fillOpacity, lineOpacity, lineWidth, pointOpacity, pointRadius, showFills, showLines, showPoints, onStyleChange]);

  const archive = source.pmtilesArchive;
  const available = source.availablePmtilesSourceLayers;
  const selected = source.selectedPmtilesSourceLayer;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <Section title="PMTiles Archive">
        {archive ? (
          <dl style={dlStyle}>
            {archive.tileType && <DlEntry label="Tile type" value={archive.tileType} />}
            {archive.minZoom != null && <DlEntry label="Min zoom" value={String(archive.minZoom)} />}
            {archive.maxZoom != null && <DlEntry label="Max zoom" value={String(archive.maxZoom)} />}
            {archive.numAddressedTiles != null && <DlEntry label="Addressed tiles" value={String(archive.numAddressedTiles)} />}
            {archive.numTileContents != null && <DlEntry label="Tile contents" value={String(archive.numTileContents)} />}
            {archive.bounds && <DlEntry label="Bounds" value={archive.bounds.map((b) => b.toFixed(4)).join(', ')} />}
          </dl>
        ) : (
          <Text variant="body">No archive metadata available.</Text>
        )}
      </Section>
      {available && available.length > 0 && (
        <Section title="Source Layer (diagnostic)">
          <select
            id={`${uid}-sl`}
            value={selected ?? ''}
            disabled={!onSourceLayerChange}
            onChange={(e) => onSourceLayerChange?.(e.target.value)}
            style={selectStyle}
            title={onSourceLayerChange ? undefined : 'Runtime source-layer switching requires a layer remount (not yet wired)'}
          >
            {available.map((sl) => <option key={sl} value={sl}>{sl}</option>)}
          </select>
        </Section>
      )}
      <Section title="Diagnostics">
        <dl style={dlStyle}>
          <DlEntry label="Render path" value={renderPath.renderPath ?? 'unknown'} />
          {renderPath.decodedTileCount != null && <DlEntry label="Decoded tiles" value={String(renderPath.decodedTileCount)} />}
          {renderPath.renderedFeatureCount != null && <DlEntry label="Rendered features" value={String(renderPath.renderedFeatureCount)} />}
          {renderPath.lastTileError && <DlEntry label="Last tile error" value={renderPath.lastTileError} />}
        </dl>
      </Section>
      <Section title="Visualization">
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>
          <Row label="Color"><input type="color" value={color} onChange={(e) => { setColor(e.target.value); emit({ color: e.target.value }); }} style={{ width: 32, height: 24, border: 'none', cursor: 'pointer' }} /></Row>
          <Slider label="Fill opacity" value={fillOpacity} onChange={(v) => { setFillOpacity(v); emit({ fillOpacity: v }); }} />
          <Slider label="Line opacity" value={lineOpacity} onChange={(v) => { setLineOpacity(v); emit({ lineOpacity: v }); }} />
          <Slider label="Line width" value={lineWidth} min={0.5} max={10} step={0.5} onChange={(v) => { setLineWidth(v); emit({ lineWidth: v }); }} />
          <Slider label="Point opacity" value={pointOpacity} onChange={(v) => { setPointOpacity(v); emit({ pointOpacity: v }); }} />
          <Slider label="Point radius" value={pointRadius} min={1} max={20} step={1} onChange={(v) => { setPointRadius(v); emit({ pointRadius: v }); }} />
          <Toggle label="Show fills" checked={showFills} onChange={(v) => { setShowFills(v); emit({ showFills: v }); }} />
          <Toggle label="Show lines" checked={showLines} onChange={(v) => { setShowLines(v); emit({ showLines: v }); }} />
          <Toggle label="Show points" checked={showPoints} onChange={(v) => { setShowPoints(v); emit({ showPoints: v }); }} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return <div><Text variant="body" style={{ marginBottom: tokens.space[2] }}>{title}</Text>{children}</div>;
}
function DlEntry({ label, value }: { readonly label: string; readonly value: string }) {
  return <><dt style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>{label}</dt><dd style={{ margin: 0, fontSize: tokens.font.size.xs, wordBreak: 'break-all' }}>{value}</dd></>;
}
function Row({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><Text variant="body">{label}</Text>{children}</div>;
}
function Slider({
  label,
  value,
  min = 0,
  max = 1,
  step = 0.01,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly onChange: (value: number) => void;
}) {
  return <Row label={label}><input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: 100 }} /></Row>;
}
function Toggle({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return <Row label={label}><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /></Row>;
}

const dlStyle = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: `${tokens.space[1]} ${tokens.space[3]}`, margin: 0 };
const selectStyle = { width: '100%', padding: tokens.space[2], fontSize: tokens.font.size.sm, borderRadius: tokens.radius.sm, border: tokens.border.thin, background: tokens.color.surface[1], color: tokens.color.ink.primary };
